import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash-es";
import { useTranslation } from "locales";
import { fetchAutocomplete, fetchGifPage, GIF_API_AVAILABLE, GIF_PROVIDER_NAME, GifResult } from "./api";
import useGifRecent from "./useGifRecent";
import "./style.scss";

interface Category {
  id: string;
  labelKey: string;
  // null → local (Recent/Favorites); "" → trending; otherwise a query sent to the provider.
  searchTerm: string | null;
  icon?: string;
}

const CATEGORIES: Category[] = [
  { id: "recent", labelKey: "Recent", searchTerm: null, icon: "🕑" },
  { id: "favorites", labelKey: "Favorites", searchTerm: null, icon: "★" },
  { id: "trending", labelKey: "Trending", searchTerm: "" },
  { id: "reactions", labelKey: "Reactions", searchTerm: "reaction" },
  { id: "love", labelKey: "Love", searchTerm: "love" },
  { id: "laugh", labelKey: "Laugh", searchTerm: "laugh" },
  { id: "sad", labelKey: "Sad", searchTerm: "sad" },
  { id: "dance", labelKey: "Dance", searchTerm: "dance" },
  { id: "yes", labelKey: "Yes", searchTerm: "yes" },
  { id: "no", labelKey: "No", searchTerm: "no" },
  { id: "omg", labelKey: "OMG", searchTerm: "omg" },
];

interface Props {
  onSelect: (url: string) => void;
}

function GifPicker({ onSelect }: Props) {
  const { t } = useTranslation();
  const { recent, favorites, addRecent, toggleFavorite } = useGifRecent();
  const favoriteIds = useMemo(() => new Set(favorites.map((g) => g.id)), [favorites]);
  const [categoryId, setCategoryId] = useState("trending");
  const [query, setQuery] = useState("");
  const [gifs, setGifs] = useState<GifResult[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  // Start in "loading" when the default tab (Trending) will trigger an immediate
  // fetch on mount — avoids a one-frame flash of the empty-state message.
  const [loading, setLoading] = useState(GIF_API_AVAILABLE);
  const [loadingMore, setLoadingMore] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [preview, setPreview] = useState<GifResult | null>(null);

  const pageAbortRef = useRef<AbortController | null>(null);
  const autocompleteAbortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Category drives the query sent to the API; a user-typed `query` always overrides it.
  const activeCategory = useMemo(
    () => CATEGORIES.find((c) => c.id === categoryId) ?? CATEGORIES.find((c) => c.id === "trending")!,
    [categoryId]
  );
  const isLocalCategory = activeCategory.searchTerm === null && !query.trim();
  const effectiveSearchTerm = query.trim() ? query.trim() : activeCategory.searchTerm ?? "";

  const loadPage = useCallback(async (searchTerm: string, cursorArg: string | null, append: boolean) => {
    if (!GIF_API_AVAILABLE) {
      setGifs([]);
      setCursor(null);
      return;
    }
    pageAbortRef.current?.abort();
    pageAbortRef.current = new AbortController();
    const signal = pageAbortRef.current.signal;
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const page = await fetchGifPage(searchTerm, cursorArg, signal);
      setGifs((prev) => (append ? [...prev, ...page.items] : page.items));
      setCursor(page.nextCursor);
    } catch (e: any) {
      if (e?.name === "AbortError") return;
      console.error("GIF API error:", e);
      if (!append) { setGifs([]); setCursor(null); }
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, []);

  // On local categories we abort any in-flight fetch and wipe `gifs` so stale
  // search results can't briefly flash when the user toggles back to a non-local tab.
  useEffect(() => {
    if (isLocalCategory) {
      pageAbortRef.current?.abort();
      setGifs([]);
      setCursor(null);
      setLoading(false);
      setLoadingMore(false);
      return;
    }
    scrollRef.current?.scrollTo({ top: 0 });
    loadPage(effectiveSearchTerm, null, false);
  }, [effectiveSearchTerm, isLocalCategory, loadPage]);

  const debouncedAutocomplete = useMemo(
    () => debounce((q: string) => {
      autocompleteAbortRef.current?.abort();
      autocompleteAbortRef.current = new AbortController();
      fetchAutocomplete(q, autocompleteAbortRef.current.signal)
        .then(setSuggestions)
        .catch((e) => { if (e?.name !== "AbortError") setSuggestions([]); });
    }, 250),
    []
  );

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setSuggestions([]);
      debouncedAutocomplete.cancel();
      return;
    }
    debouncedAutocomplete(trimmed);
  }, [query, debouncedAutocomplete]);

  // Infinite scroll — fires when the bottom sentinel enters the scroll viewport.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || isLocalCategory || !cursor) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !loading && !loadingMore) {
        loadPage(effectiveSearchTerm, cursor, true);
      }
    }, { root: scrollRef.current, threshold: 0.5 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [cursor, loading, loadingMore, effectiveSearchTerm, isLocalCategory, loadPage]);

  useEffect(() => {
    return () => {
      pageAbortRef.current?.abort();
      autocompleteAbortRef.current?.abort();
      debouncedAutocomplete.cancel();
    };
  }, [debouncedAutocomplete]);

  const handleCategoryClick = useCallback((id: string) => {
    setCategoryId(id);
    setQuery("");
    setSuggestions([]);
  }, []);

  const handleSuggestionClick = useCallback((s: string) => {
    setQuery(s);
    setSuggestions([]);
  }, []);

  const handleItemClick = useCallback((gif: GifResult) => {
    setPreview(gif);
  }, []);

  const handleSendFromPreview = useCallback(() => {
    if (!preview) return;
    addRecent(preview);
    onSelect(preview.fullUrl);
    setPreview(null);
  }, [preview, addRecent, onSelect]);

  const handleToggleFavorite = useCallback((e: React.MouseEvent, gif: GifResult) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(gif);
  }, [toggleFavorite]);

  const displayedItems = isLocalCategory
    ? (categoryId === "favorites" ? favorites : recent)
    : gifs;

  const emptyMessage = !GIF_API_AVAILABLE
    ? t("GIF API key not configured")
    : categoryId === "recent" && isLocalCategory ? t("No recent GIFs yet")
    : categoryId === "favorites" && isLocalCategory ? t("No favourites yet — tap the star on any GIF to save it")
    : t("No GIFs found");

  return (
    <div className="gif-picker">
      <div className="gif-picker__search">
        <input
          type="text"
          placeholder={t("Search GIFs...")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {suggestions.length > 0 && (
          <div className="gif-picker__suggestions">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="gif-picker__suggestion"
                onClick={() => handleSuggestionClick(s)}
              >
                {s}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="gif-picker__tabs">
        {CATEGORIES.map((cat) => {
          const active = cat.id === categoryId && !query.trim();
          return (
            <button
              key={cat.id}
              type="button"
              className={`gif-picker__tab${active ? " __active" : ""}`}
              onClick={() => handleCategoryClick(cat.id)}
            >
              {cat.icon && <span className="gif-picker__tab-icon">{cat.icon}</span>}
              {t(cat.labelKey)}
            </button>
          );
        })}
      </div>
      <div className="gif-picker__grid" ref={scrollRef}>
        {loading && displayedItems.length === 0 && (
          <div className="gif-picker__loading">{t("Loading...")}</div>
        )}
        {!loading && displayedItems.length === 0 && (
          <div className="gif-picker__empty">{emptyMessage}</div>
        )}
        {displayedItems.map((gif) => (
          <div key={gif.id} className="gif-picker__item-wrap">
            <button
              type="button"
              className="gif-picker__item"
              onClick={() => handleItemClick(gif)}
              title={gif.title}
            >
              <img src={gif.previewUrl} alt={gif.title} loading="lazy" decoding="async" />
            </button>
            <button
              type="button"
              className={`gif-picker__item-star${favoriteIds.has(gif.id) ? " __active" : ""}`}
              onClick={(e) => handleToggleFavorite(e, gif)}
              aria-label={t("Toggle favourite")}
            >
              ★
            </button>
          </div>
        ))}
        {!isLocalCategory && cursor && (
          <div ref={sentinelRef} className="gif-picker__sentinel">
            {loadingMore && <span className="gif-picker__loading-inline">{t("Loading...")}</span>}
          </div>
        )}
      </div>
      {preview && (
        <div className="gif-picker__preview-overlay" onClick={() => setPreview(null)}>
          <div className="gif-picker__preview" onClick={(e) => e.stopPropagation()}>
            <img src={preview.fullUrl} alt={preview.title} />
            <div className="gif-picker__preview-actions">
              <button
                type="button"
                className={`gif-picker__preview-star${favoriteIds.has(preview.id) ? " __active" : ""}`}
                onClick={() => toggleFavorite(preview)}
                aria-label={t("Toggle favourite")}
              >★</button>
              <button
                type="button"
                className="gif-picker__preview-cancel"
                onClick={() => setPreview(null)}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="gif-picker__preview-send"
                onClick={handleSendFromPreview}
              >
                {t("Send")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="gif-picker__powered">
        Powered by {GIF_PROVIDER_NAME}
      </div>
    </div>
  );
}

export default GifPicker;
